/**
 * Debounce a value.
 *
 * Extracted while decomposing `ExtrasManager`. The pattern
 *
 *     const [debounced, setDebounced] = useState(value);
 *     useEffect(() => {
 *       const t = setTimeout(() => setDebounced(value), delay);
 *       return () => clearTimeout(t);
 *     }, [value]);
 *
 * is easy to write and easy to write *subtly wrong* — the cleanup is what makes
 * it a debounce rather than a queue of pending updates, and it is the part
 * people drop.
 *
 * Kept generic and dependency-light on purpose: it is a candidate for the other
 * hand-rolled debounces in the app, but this commit only converts the one it
 * was extracted from, so each conversion can be verified on its own.
 */
import { useEffect, useState } from 'react';

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    // Load-bearing: without this, a fast typist queues one update per
    // keystroke and every one of them fires.
    return () => clearTimeout(t);
  }, [value, delayMs]);

  return debounced;
}
